import { ref, watch, nextTick } from 'vue'
import {
  useTable,
  tableFeatures,
  rowSortingFeature,
  rowSelectionFeature,
  rowExpandingFeature,
  columnVisibilityFeature,
  columnOrderingFeature,
  columnSizingFeature,
  createSortedRowModel,
  createExpandedRowModel,
  sortFns
} from '@tanstack/vue-table'

const features = tableFeatures({
  rowSortingFeature,
  rowSelectionFeature,
  rowExpandingFeature,
  columnVisibilityFeature,
  columnOrderingFeature,
  columnSizingFeature,
  sortedRowModel: createSortedRowModel(),
  expandedRowModel: createExpandedRowModel(),
  sortFns
})

const getSize = (size = 100, max = Number.MAX_SAFE_INTEGER, min = 40) => Math.max(Math.min(size, max), min)

/**
 * Calculates the sizing of table columns and distributes available width proportionally.
 * This function acts as an extension for TanStack Table, ensuring proper column sizing
 * based on provided metadata, including `grow`, `widthPercentage`, and size constraints.
 *
 * @template DataType - The generic type of data used in the table rows.
 *
 * @param {Header<DataType, unknown>[]} headers - An array of column headers. Each header contains
 *   metadata about the column, including size, constraints, and growth behavior.
 * @param {number} totalWidth - The total width available for the table, including padding and margins.
 *
 * @returns {Record<string, number>} An object mapping column IDs to their calculated sizes.
 */
const calculateTableSizing = (headers, totalWidth) => {
  let totalAvailableWidth = totalWidth
  let totalGrow = 0

  headers.forEach(header => {
    const { columnDef } = header.column
    if (!columnDef.size && !columnDef.meta?.grow) {
      const calculatedSize = columnDef?.meta?.widthPercentage
        ? columnDef.meta.widthPercentage * totalWidth * 0.01
        : totalWidth / headers.length
      columnDef.size = getSize(calculatedSize, columnDef.maxSize, columnDef.minSize)
    }

    if (columnDef.meta?.grow) totalGrow += 1
    else totalAvailableWidth -= getSize(columnDef.size, columnDef.maxSize, columnDef.minSize)
  })

  const sizing = {}

  headers.forEach(header => {
    const { columnDef } = header.column
    if (columnDef.meta?.grow) {
      const calculatedSize = Math.floor(totalAvailableWidth / totalGrow)
      columnDef.size = getSize(calculatedSize, columnDef.maxSize, columnDef.minSize)
    }
    // console.log('%ccolumnDef', 'color:magenta', columnDef)

    sizing[`${columnDef.id}`] = Number(columnDef.size)
  })

  return sizing
}

/**
 * Wraps @tanstack/vue-table with controlled state for visibility, ordering,
 * row selection, and (manual/server-side) sorting.
 */
export const useGrid = ({
  columns,
  rows,
  initialVisibility = {},
  initialOrder = [],
  clientSort = null,
  getSubRows = null,
  getRowCanExpand = null,
  defaultExpanded = false,
  getRowId = null
}) => {
  const sorting = ref([])
  const selectedRows = ref({})
  const columnVisibility = ref({ ...initialVisibility })
  const columnOrder = ref([...initialOrder])
  const expanded = ref(defaultExpanded)

  const table = useTable({
    features,
    get data () {
      return rows.value ?? []
    },
    get columns () {
      return columns.value ?? []
    },
    get manualSorting () {
      return !(clientSort?.value ?? false)
    },
    getSubRows: getSubRows ?? undefined,
    getRowCanExpand: getRowCanExpand ?? undefined,
    getRowId: getRowId ?? undefined,
    // Without this, expanded state resets to {} (collapsed) every time `rows`
    // changes — including the very first time async data replaces the initial
    // empty array, which silently wipes out a `defaultExpanded: true` consumer.
    autoResetExpanded: false,
    // useGrid registers no pagination row model, so getRowModel() always falls
    // through to getExpandedRowModel() — but that stage only actually flattens
    // subRows into the row list when paginateExpandedRows (or manualPagination)
    // is true. Without this, toggling `expanded` would have no visible effect.
    paginateExpandedRows: true,
    enableMultiSort: false,
    enableSortingRemoval: true,
    enableRowSelection: true,
    state: {
      get sorting () {
        return sorting.value
      },
      get rowSelection () {
        return selectedRows.value
      },
      get columnVisibility () {
        return columnVisibility.value
      },
      get columnOrder () {
        return columnOrder.value
      },
      get expanded () {
        return expanded.value
      }
    },
    onSortingChange: updater => {
      sorting.value = typeof updater === 'function' ? updater(sorting.value) : updater
    },
    onRowSelectionChange: updater => {
      selectedRows.value = typeof updater === 'function' ? updater(selectedRows.value) : updater
    },
    onColumnVisibilityChange: updater => {
      columnVisibility.value = typeof updater === 'function' ? updater(columnVisibility.value) : updater
    },
    onColumnOrderChange: updater => {
      columnOrder.value = typeof updater === 'function' ? updater(columnOrder.value) : updater
    },
    onExpandedChange: updater => {
      expanded.value = typeof updater === 'function' ? updater(expanded.value) : updater
    }
  })

  const columnSizing = async ({ width }) => {
    await nextTick()
    const headers = table.getHeaderGroups()[0].headers
    const initialColumnSizing = calculateTableSizing(headers, width)
    table.setColumnSizing(initialColumnSizing)
  }

  watch(() => initialVisibility, val => {
    columnVisibility.value = { ...val }
  }, { deep: true })

  watch(() => initialOrder, val => {
    if (val.length) columnOrder.value = [...val]
  }, { deep: true })

  return { table, sorting, selectedRows, columnVisibility, columnOrder, expanded, columnSizing }
}
